"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useRef } from "react";
import { Cog6ToothIcon } from "@heroicons/react/24/outline";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiUpdateWaveRequest } from "@/generated/models/ApiUpdateWaveRequest";
import type { WaveGroupType } from "../../../WaveGroup.types";
import WaveGroupEditButton, { type WaveGroupEditButtonHandle } from "../../WaveGroupEditButton";
import WaveGroupRemoveButton, { type WaveGroupRemoveButtonHandle } from "../../WaveGroupRemoveButton";
import { CompactMenu, CompactMenuItem } from "@/components/compact-menu";
const GROUP_OPTIONS_LABEL = "Group options";
interface WaveGroupEditMenuProps {
  readonly wave: ApiWave;
  readonly type: WaveGroupType;
  readonly onWaveUpdate: (body: ApiUpdateWaveRequest, opts?: {
    readonly skipAuth?: boolean | undefined;
  }) => Promise<void>;
  readonly hasGroup: boolean;
  readonly canIncludeIdentity: boolean;
  readonly canExcludeIdentity: boolean;
  readonly canRemoveGroup: boolean;
  readonly onIncludeIdentity: () => void;
  readonly onExcludeIdentity: () => void;
  readonly onChangeGroup?: (() => void) | undefined;
  readonly onRemoveGroup?: (() => void) | undefined;
}
interface WaveGroupEditMenuTriggerProps {
  readonly label: string;
}
function WaveGroupEditMenuTrigger(t0) {
  const $ = _c(5);
  const {
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="tw-sr-only">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Cog6ToothIcon className="tw-size-5 tw-flex-shrink-0" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <>{t1}{t2}</>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
export default function WaveGroupEditMenu({
  wave,
  type,
  onWaveUpdate,
  hasGroup,
  canIncludeIdentity,
  canExcludeIdentity,
  canRemoveGroup,
  onIncludeIdentity,
  onExcludeIdentity,
  onChangeGroup,
  onRemoveGroup
}: WaveGroupEditMenuProps) {
  const editButtonRef = useRef<WaveGroupEditButtonHandle | null>(null);
  const removeButtonRef = useRef<WaveGroupRemoveButtonHandle | null>(null);
  const menuItems = useMemo<CompactMenuItem[]>(() => {
    const items: CompactMenuItem[] = [];
    const changeGroupLabel = hasGroup ? "Change group" : "Add group";
    if (canIncludeIdentity) {
      items.push({
        id: "include",
        label: "Include identity",
        onSelect: onIncludeIdentity
      });
    }
    if (canExcludeIdentity) {
      items.push({
        id: "exclude",
        label: "Exclude identity",
        onSelect: onExcludeIdentity
      });
    }
    items.push({
      id: "change",
      label: changeGroupLabel,
      onSelect: () => {
        if (onChangeGroup) {
          onChangeGroup();
          return;
        }
        editButtonRef.current?.open();
      }
    });
    if (canRemoveGroup) {
      items.push({
        id: "remove",
        label: "Remove group",
        onSelect: () => {
          if (onRemoveGroup) {
            onRemoveGroup();
            return;
          }
          removeButtonRef.current?.open();
        },
        className: "tw-text-red desktop-hover:hover:tw-text-red"
      });
    }
    return items;
  }, [canIncludeIdentity, canExcludeIdentity, onIncludeIdentity, onExcludeIdentity, onChangeGroup, onRemoveGroup, canRemoveGroup, hasGroup]);
  return <div className="tw-relative">
      <CompactMenu triggerClassName="tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-iron-500 desktop-hover:hover:tw-text-iron-300 hover:tw-bg-iron-800 tw-transition tw-duration-300 tw-ease-out" trigger={<WaveGroupEditMenuTrigger label={GROUP_OPTIONS_LABEL} />} aria-label={GROUP_OPTIONS_LABEL} items={menuItems} />
      <WaveGroupEditButton ref={editButtonRef} wave={wave} type={type} onWaveUpdate={onWaveUpdate} renderTrigger={null} />
      {canRemoveGroup ? <WaveGroupRemoveButton ref={removeButtonRef} wave={wave} type={type} onWaveUpdate={onWaveUpdate} renderTrigger={null} /> : null}
    </div>;
}